/**
 * <summary>
 * UploadTempaltes page lets store view and choose the Headquarter before and after templates in medical records.
 * fetches data, handles form submission, and shows success or error toasts.
 * Developer: Pratiksha Sharma
 * </summary>
 */
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../contexts/store-provider";
import { getHeadquarterIdFromLocalStorage } from "../../helpers/localstorage-helper";
import {
  IUpdateHeadquarterTemplate
} from "../../models/response/IHeadquarterResponse";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import AddEditUploadTemplatesForm from "./forms/add-edit-upload-template-form";

const UploadTemplates = () => {
  const { headquarterStore } = useStore();
  const {
    headquarterDetails,
    UpdateHeadquarterTemplateService,
    addUpdateHeadquarterTemplateState,
    GetHeadquarterByIdService,
    resetAddUpdateHeadquarterTemplateState,
    GetHeadquarterTemplatesByIdService,
    resetHeadquarterTemplatesState,
    headquarterTemplateDetails,
    headquarterTemplatesState,
    getHeadquartertByIdState,
    disableAction,
    setDisableAction
  } = headquarterStore;

  const HeadquaterId = getHeadquarterIdFromLocalStorage();

  const isUpdateMode = headquarterDetails?.Id > 0;

  useEffect(() => {
    if (HeadquaterId > 0) {
      GetHeadquarterTemplatesByIdService(HeadquaterId);
      GetHeadquarterByIdService(HeadquaterId);
    }
  }, [HeadquaterId]);

  useEffect(() => {
    return () => {
      resetHeadquarterTemplatesState();
      setDisableAction(false);
    };
  }, []);

  useEffect(() => {
    if (addUpdateHeadquarterTemplateState.success) {
      const hadIconsBefore =
        !!headquarterTemplateDetails?.TemplateBeforeOne ||
        !!headquarterTemplateDetails?.TemplateBeforeTwo ||
        !!headquarterTemplateDetails?.TemplateAfterOne ||
        !!headquarterTemplateDetails?.TemplateAfterTwo;

      toast.success(
        <FormattedMessage
          id={hadIconsBefore ? "updated_success" : "added_success"}
        />
      );

      GetHeadquarterByIdService(HeadquaterId);
      GetHeadquarterTemplatesByIdService(HeadquaterId);
      resetAddUpdateHeadquarterTemplateState();
    }
  }, [addUpdateHeadquarterTemplateState.success]);

  useEffect(() => {
    if (addUpdateHeadquarterTemplateState.error) {
      toast.error(
        <FormattedMessage id={addUpdateHeadquarterTemplateState.error} />
      );
      resetAddUpdateHeadquarterTemplateState();
    }
  }, [addUpdateHeadquarterTemplateState.error]);

  const submitHandler = (data: IUpdateHeadquarterTemplate) => {
    const updateData: IUpdateHeadquarterTemplate = {
      Id: data.Id,
      TemplateBeforeOne: data.TemplateBeforeOne,
      TemplateBeforeTwo: data.TemplateBeforeTwo,
      TemplateAfterOne: data.TemplateAfterOne,
      TemplateAfterTwo: data.TemplateAfterTwo,
    };
    UpdateHeadquarterTemplateService(updateData);
  };

  return (
    <>
      {(headquarterTemplatesState.inProgress ||
        getHeadquartertByIdState.inProgress ||
        addUpdateHeadquarterTemplateState.inProgress) && (
        <FormLoader
          loading={
            headquarterTemplatesState.inProgress ||
            getHeadquartertByIdState.inProgress ||
            addUpdateHeadquarterTemplateState.inProgress
          }
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="upload_templates" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="various_settings" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="upload_templates" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row align-items-center justify-content-center">
          <div className="col-md-7 col-lg-6 col-xl-4 col-xxl-3 col-12">
            <div className="card">
              <div className="card-header p-3">
                <h3 className="card-title mb-0">
                  <FormattedMessage id="upload_templates" />
                </h3>
              </div>
              <div className="card-body">
                {addUpdateHeadquarterTemplateState.inProgress && (
                  <FormLoader
                    loading={addUpdateHeadquarterTemplateState.inProgress}
                  />
                )}
                <AddEditUploadTemplatesForm
                  id={HeadquaterId}
                  initialValues={headquarterTemplateDetails}
                  submitHandler={submitHandler}
                  loading={addUpdateHeadquarterTemplateState.inProgress}
                  isUpdateMode={isUpdateMode}
                  disableAction={disableAction}
                  addUpdateHeadquarterTemplateState={
                    addUpdateHeadquarterTemplateState
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(UploadTemplates);
